import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/** Vérification anti-robot active si une clé de site est configurée. */
export const TURNSTILE_ENABLED = !!SITE_KEY;

let scriptPromise = null;
function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => { scriptPromise = null; reject(new Error('turnstile')); };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile. onVerify(token) reçoit le jeton, puis '' quand il
 * expire ou échoue. Un jeton ne sert qu'une fois : appeler ref.reset() après
 * chaque envoi de formulaire (sinon la tentative suivante est refusée).
 */
const TurnstileWidget = forwardRef(({ onVerify, onError }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;
  const [status, setStatus] = useState('loading');

  const render = useCallback(async () => {
    if (!SITE_KEY) return;
    setStatus('loading');
    try {
      await loadScript();
    } catch {
      setStatus('error');
      return;
    }
    if (!containerRef.current || !window.turnstile) return;
    if (widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      language: 'fr',
      callback: (token) => { setStatus('verified'); onVerifyRef.current && onVerifyRef.current(token); },
      'expired-callback': () => { setStatus('expired'); onVerifyRef.current && onVerifyRef.current(''); },
      'timeout-callback': () => { setStatus('expired'); onVerifyRef.current && onVerifyRef.current(''); },
      'error-callback': () => {
        setStatus('error');
        onVerifyRef.current && onVerifyRef.current('');
        onErrorRef.current && onErrorRef.current();
      },
    });
    setStatus('ready');
  }, []);

  useEffect(() => {
    render();
    return () => {
      if (widgetIdRef.current && window.turnstile && window.turnstile.remove) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [render]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      onVerifyRef.current && onVerifyRef.current('');
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setStatus('ready');
      } else {
        render();
      }
    },
  }), [render]);

  if (!SITE_KEY) return null;

  return (
    <div>
      <div ref={containerRef} style={{ minHeight: 65, display: 'flex', justifyContent: 'center' }} />
      {status === 'loading' && (
        <p role="status" style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', margin: '4px 0 0' }}>
          Chargement de la vérification de sécurité…
        </p>
      )}
      {(status === 'error' || status === 'expired') && (
        <p role="alert" style={{ fontSize: 12, color: '#B91C1C', textAlign: 'center', margin: '4px 0 0' }}>
          {status === 'error'
            ? 'La vérification de sécurité n\'a pas pu se charger (connexion lente, bloqueur de publicité…). '
            : 'La vérification de sécurité a expiré. '}
          <button type="button" onClick={render} style={{ background: 'none', border: 0, padding: 0, color: 'inherit', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
            Réessayer
          </button>
        </p>
      )}
    </div>
  );
});

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
